import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import "./styles/Sales.css";

const PAYMENT_API = "https://okna.co.tz/mrms/Payment.php";
const MENU_API = "https://okna.co.tz/mrms/Menu.php";

export default function AdminSalesReport() {
  const today = new Date().toISOString().split("T")[0];

  const [date, setDate] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("token");

  // Helper function for authenticated fetch
  const authFetch = async (url, options = {}) => {
    if (!token) {
      throw new Error("No token found");
    }
    
    // Add token to URL
    const separator = url.includes('?') ? '&' : '?';
    const urlWithToken = `${url}${separator}token=${token}`;
    
    const response = await fetch(urlWithToken, {
      ...options,
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers
      }
    });
    
    // Handle 401 Unauthorized
    if (response.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("staff");
      window.location.href = "/";
      throw new Error("Session expired");
    }
    
    return response;
  };

  // Format date as DD-MMM-YY (e.g., 18-Mar-26)
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch menu items
      console.log("Fetching menu...");
      const menuRes = await authFetch(MENU_API);
      let menu = await menuRes.json();
      
      // Handle menu response - it could be an array or an object with error
      if (menu.error) {
        console.error("Menu API error:", menu.error);
        menu = [];
      } else if (!Array.isArray(menu)) {
        console.error("Menu API returned non-array:", menu);
        menu = [];
      }
      
      console.log(`Menu loaded: ${menu.length} items`);

      // Fetch payments
      console.log("Fetching payments...");
      const payRes = await authFetch(PAYMENT_API);
      let payments = await payRes.json();
      
      // Handle payments response
      if (payments.error) {
        console.error("Payment API error:", payments.error);
        payments = [];
      } else if (!Array.isArray(payments)) {
        console.error("Payment API returned non-array:", payments);
        payments = [];
      }
      
      console.log(`Payments loaded: ${payments.length}`);

      const selectedDate = new Date(date);
      
      // Log for debugging
      console.log("Selected Date:", selectedDate);
      console.log("All payments:", payments.map(p => ({
        id: p.order_id,
        order_number: p.order_number,
        created_at: p.created_at,
        status: p.status,
        payment_method: p.payment_method
      })));

      /* FILTER PAYMENTS - Only PAID payments for selected date */
      const filteredPayments = payments.filter((p) => {
        if (!p.created_at) return false;
        if (p.status !== "PAID") return false;
        
        const paymentDate = new Date(p.created_at);
        
        // Compare dates without time component
        return paymentDate.getFullYear() === selectedDate.getFullYear() &&
               paymentDate.getMonth() === selectedDate.getMonth() &&
               paymentDate.getDate() === selectedDate.getDate();
      });

      console.log(`Filtered payments count: ${filteredPayments.length}`);
      console.log("Filtered payments:", filteredPayments);

      if (filteredPayments.length === 0) {
        setReport(null);
        alert(`No data found for ${formatDate(date)}. Please check if there are any paid orders for this date.`);
        setLoading(false);
        return;
      }

      /* HOURLY - Start from 5:00 */
      const hourlyMap = {};

      // Create hours from 5:00 to 4:00 (next day)
      for (let i = 5; i < 29; i++) {
        const hour24 = i % 24;
        const startHour = hour24.toString().padStart(2, "0") + ":00";
        const endHour = ((hour24 + 1) % 24).toString().padStart(2, "0") + ":00";
        const range = `${startHour}-${endHour}`;
        hourlyMap[range] = { range, items: 0, value: 0 };
      }

      /* ITEMS MAP */
      const itemsMap = {};
      let totalItems = 0;

      /* STAFF PERFORMANCE MAP */
      const staffMap = {};

      filteredPayments.forEach((payment) => {
        const paymentDate = new Date(payment.created_at);
        const paymentHour = paymentDate.getHours();
        
        // Find the correct hour range (5:00-6:00, 6:00-7:00, etc.)
        let foundRange = null;
        for (let i = 5; i < 29; i++) {
          const startHour = i % 24;
          const endHour = (i + 1) % 24;
          
          if (paymentHour === startHour) {
            const startStr = startHour.toString().padStart(2, "0") + ":00";
            const endStr = endHour.toString().padStart(2, "0") + ":00";
            foundRange = `${startStr}-${endStr}`;
            break;
          }
        }

        const qty = parseInt(payment.quantity_total) || 0;
        const amount = parseFloat(payment.total_amount) || 0;

        if (foundRange && hourlyMap[foundRange]) {
          hourlyMap[foundRange].items += qty;
          hourlyMap[foundRange].value += amount;
        }

        totalItems += qty;

        /* STAFF PERFORMANCE */
        const staffName = payment.staff_name || "Unknown";
        if (!staffMap[staffName]) {
          staffMap[staffName] = {
            name: staffName,
            orders: [],
            totalItems: 0,
            totalValue: 0,
          };
        }

        /* PARSE ITEMS JSON */
        if (payment.items) {
          let items = [];

          try {
            items =
              typeof payment.items === "string"
                ? JSON.parse(payment.items)
                : payment.items;
          } catch (e) {
            console.error("Error parsing items for payment", payment.order_id, e);
          }

          // Ensure items is an array
          if (!Array.isArray(items)) {
            items = [];
          }

          /* Add items to staff performance */
          items.forEach((item) => {
            const itemName = item.item_name || "Unknown Item";
            const quantity = parseInt(item.quantity) || 1;
            const price = parseFloat(item.price) || 0;

            staffMap[staffName].orders.push({
              orderId: payment.order_id || "N/A",
              orderNumber: payment.order_number || payment.order_id || "N/A",
              time: paymentDate.toLocaleTimeString(),
              itemName,
              quantity,
              price,
              paymentStatus: payment.payment_status || "PAID"
            });

            staffMap[staffName].totalItems += quantity;
            staffMap[staffName].totalValue += price * quantity;
          });

          /* Process items for itemsMap */
          items.forEach((item) => {
            const name = item.item_name || "Unknown Item";
            const quantity = parseInt(item.quantity) || 1;
            const price = parseFloat(item.price) || 0;

            if (!itemsMap[name]) {
              const menuItem = Array.isArray(menu) ? menu.find((m) => m.item_name === name) : null;

              itemsMap[name] = {
                code: menuItem?.id || `ITEM${Object.keys(itemsMap).length + 1}`,
                name,
                qty: 0,
                total: 0,
                group: menuItem?.group_button || "Uncategorized",
              };
            }

            itemsMap[name].qty += quantity;
            itemsMap[name].total += price * quantity;
          });
        }
      });

      /* SORT ITEMS HIGH → LOW */
      const rankedItems = Object.values(itemsMap).sort((a, b) => b.qty - a.qty);

      /* Calculate items totals */
      const totalItemsQty = rankedItems.reduce((sum, item) => sum + item.qty, 0);
      const totalItemsAmount = rankedItems.reduce(
        (sum, item) => sum + item.total,
        0
      );

      /* TRANSACTIONS BY PAYMENT METHOD */
      const paymentMethodMap = {};

      filteredPayments.forEach((p) => {
        const method = p.payment_method || "Unknown";
        if (!paymentMethodMap[method]) {
          paymentMethodMap[method] = {
            method,
            transactions: [],
            total: 0,
            count: 0,
          };
        }

        // Use the order_number directly from the database
        const orderNumber = p.order_number || p.order_id || "N/A";

        paymentMethodMap[method].transactions.push({
          time: new Date(p.created_at).toTimeString().split(" ")[0],
          order: orderNumber,
          amount: parseFloat(p.total_amount) || 0,
          staff: p.staff_name || "Unknown",
          paymentStatus: p.payment_status || "PAID"
        });

        paymentMethodMap[method].total += parseFloat(p.total_amount) || 0;
        paymentMethodMap[method].count++;
      });

      /* TRANSACTIONS LIST */
      const transactions = filteredPayments.map((p) => {
        // Use the order_number directly from the database
        const orderNumber = p.order_number || p.order_id || "N/A";
        
        return {
          time: new Date(p.created_at).toTimeString().split(" ")[0],
          order: orderNumber,
          amount: parseFloat(p.total_amount) || 0,
          method: p.payment_method || "Unknown",
          staff: p.staff_name || "Unknown",
          paymentStatus: p.payment_status || "PAID"
        };
      });

      /* SUMMARY */
      const totalSales = filteredPayments.reduce(
        (sum, p) => sum + (parseFloat(p.total_amount) || 0),
        0
      );

      // Filter out hours with no sales and sort by time
      const hourlyWithSales = Object.values(hourlyMap)
        .filter(h => h.value > 0)
        .sort((a, b) => {
          const hourA = parseInt(a.range.split(":")[0]);
          const hourB = parseInt(b.range.split(":")[0]);
          return hourA - hourB;
        });

      setReport({
        date: selectedDate.toISOString().split("T")[0],
        formattedDate: formatDate(selectedDate.toISOString().split("T")[0]),
        hourly: hourlyWithSales,
        transactions,
        items: rankedItems,
        itemsTotals: {
          totalQty: totalItemsQty,
          totalAmount: totalItemsAmount,
        },
        paymentMethods: Object.values(paymentMethodMap),
        staffPerformance: Object.values(staffMap).sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
        summary: {
          totalSales,
          totalOrders: filteredPayments.length,
          totalItems,
          totalTransactions: filteredPayments.length,
        },
      });
      
      console.log("Report generated successfully");
      
    } catch (err) {
      console.error("Error fetching report:", err);
      setError(err.message);
      alert(`Failed to load report: ${err.message}`);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (token) {
      fetchReport();
    } else {
      setError("No authentication token found. Please login again.");
    }
  }, []);

  /* EXPORT EXCEL */
  const exportExcel = () => {
    if (!report) return;

    const wb = XLSX.utils.book_new();

    /* SUMMARY */
    const summarySheet = XLSX.utils.aoa_to_sheet([
      ["DAILY SALES REPORT"],
      [`Date: ${report.formattedDate}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      ["SUMMARY"],
      ["Total Sales", report.summary.totalSales],
      ["Total Orders", report.summary.totalOrders],
      ["Items Sold", report.summary.totalItems],
      ["Transactions", report.summary.totalTransactions],
    ]);

    XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");

    /* HOURLY - With range and value */
    const hourlySheet = XLSX.utils.aoa_to_sheet([
      ["HOURLY SALES REPORT"],
      ["Hour Range", "Items", "Value (TZS)"],
      ...report.hourly.map((h) => [h.range, h.items, h.value]),
    ]);

    XLSX.utils.book_append_sheet(wb, hourlySheet, "Hourly");

    /* PAYMENT METHODS - With summary at bottom */
    const paymentSheetData = [["PAYMENT REPORT"], []];

    // Add each payment method's transactions
    report.paymentMethods.forEach((method) => {
      paymentSheetData.push([`${method.method} PAYMENTS`]);
      paymentSheetData.push(["Time", "Order", "Amount", "Staff", "Status"]);

      method.transactions.forEach((t) => {
        paymentSheetData.push([t.time, t.order, t.amount, t.staff, t.paymentStatus]);
      });

      paymentSheetData.push([
        `${method.method} Total`,
        "",
        method.total,
        `${method.count} transactions`,
        ""
      ]);
      paymentSheetData.push([]); // Empty row for spacing
    });

    // Add overall payment summary
    paymentSheetData.push(["PAYMENT SUMMARY"]);
    paymentSheetData.push(["Payment Method", "Transactions", "Total Amount"]);
    report.paymentMethods.forEach((method) => {
      paymentSheetData.push([method.method, method.count, method.total]);
    });
    paymentSheetData.push([
      "GRAND TOTAL",
      report.summary.totalTransactions,
      report.summary.totalSales,
    ]);

    const paymentSheet = XLSX.utils.aoa_to_sheet(paymentSheetData);
    XLSX.utils.book_append_sheet(wb, paymentSheet, "Payments");

    /* ITEMS - With totals at bottom */
    const itemSheetData = [
      ["ITEMIZED SALES REPORT"],
      ["Rank", "Code", "Item", "Category", "Qty", "Total"],
      ...report.items.map((i, index) => [
        index + 1,
        i.code,
        i.name,
        i.group,
        i.qty,
        i.total,
      ]),
      [],
      ["TOTALS", "", "", "", report.itemsTotals.totalQty, report.itemsTotals.totalAmount],
    ];

    const itemSheet = XLSX.utils.aoa_to_sheet(itemSheetData);
    XLSX.utils.book_append_sheet(wb, itemSheet, "Items");

    /* STAFF PERFORMANCE */
    const staffSheetData = [["STAFF PERFORMANCE REPORT"]];

    report.staffPerformance.forEach((staff) => {
      staffSheetData.push([`\n${staff.name.toUpperCase()}`]);
      staffSheetData.push(["Order ID", "Time", "Item", "Qty", "Price", "Status"]);

      staff.orders.forEach((order) => {
        staffSheetData.push([
          order.orderNumber,
          order.time,
          order.itemName,
          order.quantity,
          order.price,
          order.paymentStatus
        ]);
      });

      staffSheetData.push([
        `${staff.name} Summary`,
        "",
        `Total Items: ${staff.totalItems}`,
        "",
        `Total Value: ${staff.totalValue}`,
        ""
      ]);
      staffSheetData.push([]); // Empty row for spacing
    });

    const staffSheet = XLSX.utils.aoa_to_sheet(staffSheetData);
    XLSX.utils.book_append_sheet(wb, staffSheet, "Staff Performance");

    XLSX.writeFile(wb, `Sales_Report_${report.date}.xlsx`);
  };

  if (error) {
    return (
      <div className="report-page">
        <div className="report-header">
          <h1>Daily POS Sales Report</h1>
        </div>
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="report-page">
      <div className="report-header">
        <h1>Daily POS Sales Report</h1>

        <div className="report-controls">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />

          <button onClick={fetchReport} disabled={loading}>
            {loading ? "Loading..." : "Generate Report"}
          </button>

          <button onClick={exportExcel} disabled={!report || loading}>
            Export Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading report data...</p>
        </div>
      )}

      {!loading && report && (
        <>
          {/* SUMMARY */}
          <section className="report-section">
            <h2>Sales Summary - {report.formattedDate}</h2>

            <div className="summary-cards">
              <div className="summary-card">
                <span>Total Sales</span>
                <strong>TZS {report.summary.totalSales.toLocaleString()}</strong>
              </div>

              <div className="summary-card">
                <span>Orders</span>
                <strong>{report.summary.totalOrders}</strong>
              </div>

              <div className="summary-card">
                <span>Items Sold</span>
                <strong>{report.summary.totalItems}</strong>
              </div>

              <div className="summary-card">
                <span>Avg Order</span>
                <strong>
                  TZS{" "}
                  {(report.summary.totalSales /
                    (report.summary.totalOrders || 1)).toFixed(0)}
                </strong>
              </div>
            </div>
          </section>

          {/* HOURLY REPORT */}
          {report.hourly.length > 0 && (
            <section className="report-section">
              <h2>Hourly Sales Report</h2>

              <table>
                <thead>
                  <tr>
                    <th>Hour Range</th>
                    <th>Items</th>
                    <th>Value (TZS)</th>
                  </tr>
                </thead>

                <tbody>
                  {report.hourly.map((h, i) => (
                    <tr key={i}>
                      <td>{h.range}</td>
                      <td>{h.items}</td>
                      <td>TZS {h.value.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* PAYMENT METHODS REPORT */}
          {report.paymentMethods.length > 0 && (
            <section className="report-section">
              <h2>Payment Methods Report</h2>

              {report.paymentMethods.map((method, idx) => (
                <div key={idx} className="payment-method-section">
                  <h3>{method.method} Payments</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Order</th>
                        <th>Amount</th>
                        <th>Staff</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {method.transactions.map((t, i) => (
                        <tr key={i}>
                          <td>{t.time}</td>
                          <td>{t.order}</td>
                          <td>TZS {t.amount.toLocaleString()}</td>
                          <td>{t.staff}</td>
                          <td>
                            <span className={`status-badge ${t.paymentStatus.toLowerCase()}`}>
                              {t.paymentStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="2">
                          <strong>{method.method} Total:</strong>
                        </td>
                        <td>
                          <strong>TZS {method.total.toLocaleString()}</strong>
                        </td>
                        <td colSpan="2">
                          <strong>({method.count} transactions)</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ))}

              {/* Payment Summary */}
              <div className="payment-summary">
                <h3>Payment Summary</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Payment Method</th>
                      <th>Transactions</th>
                      <th>Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.paymentMethods.map((method, idx) => (
                      <tr key={idx}>
                        <td>{method.method}</td>
                        <td>{method.count}</td>
                        <td>TZS {method.total.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>
                        <strong>GRAND TOTAL</strong>
                      </td>
                      <td>
                        <strong>{report.summary.totalTransactions}</strong>
                      </td>
                      <td>
                        <strong>TZS {report.summary.totalSales.toLocaleString()}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          )}

          {/* ITEMIZED RANKING WITH TOTALS */}
          {report.items.length > 0 && (
            <section className="report-section">
              <h2>Itemized Sales Ranking</h2>

              <table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Code</th>
                    <th>Item</th>
                    <th>Category</th>
                    <th>Units Sold</th>
                    <th>Total Value</th>
                  </tr>
                </thead>

                <tbody>
                  {report.items.map((item, index) => (
                    <tr key={index}>
                      <td>#{index + 1}</td>
                      <td>{item.code}</td>
                      <td>{item.name}</td>
                      <td>{item.group}</td>
                      <td>{item.qty}</td>
                      <td>TZS {item.total.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr>
                    <td colSpan="4">
                      <strong>TOTALS</strong>
                    </td>
                    <td>
                      <strong>{report.itemsTotals.totalQty}</strong>
                    </td>
                    <td>
                      <strong>TZS {report.itemsTotals.totalAmount.toLocaleString()}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </section>
          )}

          {/* STAFF PERFORMANCE */}
          {report.staffPerformance.length > 0 && (
            <section className="report-section">
              <h2>Staff Performance</h2>

              {report.staffPerformance.map((staff, idx) => (
                <div key={idx} className="staff-section">
                  <h3>{staff.name}</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Time</th>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staff.orders.map((order, i) => (
                        <tr key={i}>
                          <td>{order.orderNumber}</td>
                          <td>{order.time}</td>
                          <td>{order.itemName}</td>
                          <td>{order.quantity}</td>
                          <td>TZS {order.price.toLocaleString()}</td>
                          <td>
                            <span className={`status-badge ${order.paymentStatus.toLowerCase()}`}>
                              {order.paymentStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="2">
                          <strong>{staff.name} Summary</strong>
                        </td>
                        <td>
                          <strong>Items: {staff.totalItems}</strong>
                        </td>
                        <td colSpan="3">
                          <strong>Total: TZS {staff.totalValue.toLocaleString()}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ))}
            </section>
          )}
        </>
      )}
      
      <style jsx>{`
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 50px;
        }
        
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .error-message {
          text-align: center;
          padding: 50px;
          color: #f44336;
        }
        
        .error-message button {
          margin-top: 20px;
          padding: 10px 20px;
          background: #667eea;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }
        
        .status-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
        }
        
        .status-badge.paid {
          background: #4caf50;
          color: white;
        }
        
        .status-badge.pending {
          background: #ff9800;
          color: white;
        }
      `}</style>
    </div>
  );
}